import { useState, useEffect } from 'react';
import { adminApi, categoriesApi, uploadProductImage } from '../../api';
import api from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Package, Plus, Trash2, Loader2, Edit, Upload, FolderOpen, Key, X, Search, Link, Image as ImageIcon } from 'lucide-react';
import Pagination from '../../components/Pagination';

const formatMoney = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);

const PER_PAGE = 12;

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showLicenseDialog, setShowLicenseDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [form, setForm] = useState({ title: '', subtitle: '', description: '', price: '', discount: '0', category_id: '', thumbnail: '' });
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [licenses, setLicenses] = useState([{ content: '' }]);
  const [productLicenses, setProductLicenses] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewAll, setViewAll] = useState(false);
  const [deletingSold, setDeletingSold] = useState(false);
  const [imageInputType, setImageInputType] = useState('url'); // 'url' or 'upload'

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    let filtered = allProducts;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = allProducts.filter(product => 
        product.title?.toLowerCase().includes(query) || 
        product.category_name?.toLowerCase().includes(query)
      );
    }
    setProducts(filtered);
    setCurrentPage(1);
  }, [searchQuery, allProducts]);

  const fetchData = async () => {
    try {
      const [catRes] = await Promise.all([categoriesApi.getAll()]);
      // Admin should see ALL products including out-of-stock
      const prodListRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products?per_page=500&include_out_of_stock=true`);
      const prodData = await prodListRes.json();
      setAllProducts(prodData.data || []);
      setProducts(prodData.data || []);
      setCategories(catRes.data);
    } catch (error) { console.error('Failed to fetch data:', error); } 
    finally { setIsLoading(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const result = await uploadProductImage(file);
      setForm({ ...form, thumbnail: result.url });
      toast.success('Image uploaded');
    } catch (error) { toast.error('Failed to upload image'); }
    finally { setUploadingImage(false); }
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    try {
      const productData = { ...form, price: parseFloat(form.price), discount: parseFloat(form.discount) || 0 };
      if (selectedProduct) {
        await adminApi.updateProduct(selectedProduct.id, productData);
        toast.success('Product updated');
      } else {
        await adminApi.createProduct(productData);
        toast.success('Product created');
      }
      setShowProductDialog(false);
      resetProductForm();
      fetchData();
    } catch (error) { toast.error('Failed to save product'); }
  };

  const handleSubmitCategory = async (e) => {
    e.preventDefault();
    try {
      if (selectedCategory) {
        await categoriesApi.update(selectedCategory.id, { ...categoryForm, is_show_in_home: true });
        toast.success('Category updated');
      } else {
        await categoriesApi.create({ ...categoryForm, is_show_in_home: true });
        toast.success('Category created');
      }
      setShowCategoryDialog(false);
      setCategoryForm({ name: '', description: '' });
      setSelectedCategory(null);
      fetchData();
    } catch (error) { toast.error('Failed to save category'); }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await adminApi.deleteProduct(id); toast.success('Product deleted'); fetchData(); } 
    catch (error) { toast.error('Failed to delete'); }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try { await categoriesApi.delete(id); toast.success('Category deleted'); fetchData(); } 
    catch (error) { toast.error('Failed to delete'); }
  };

  const resetProductForm = () => {
    setForm({ title: '', subtitle: '', description: '', price: '', discount: '0', category_id: '', thumbnail: '' });
    setSelectedProduct(null);
    setImageInputType('url');
  };

  const editProduct = (product) => {
    setSelectedProduct(product);
    setForm({
      title: product.title,
      subtitle: product.subtitle || '',
      description: product.description || '',
      price: product.price.toString(),
      discount: (product.discount || 0).toString(),
      category_id: product.category_id || '',
      thumbnail: product.thumbnail || ''
    });
    // Auto-detect if existing thumbnail is URL or uploaded file
    setImageInputType(product.thumbnail?.startsWith('http') ? 'url' : 'upload');
    setShowProductDialog(true);
  };

  const editCategory = (category) => {
    setSelectedCategory(category);
    setCategoryForm({ name: category.name, description: category.description || '' });
    setShowCategoryDialog(true);
  };

  // License Management
  const handleOpenLicenseDialog = async (product) => {
    setSelectedProduct(product);
    setShowLicenseDialog(true);
    try {
      const response = await api.get(`/admin/licenses/${product.id}`);
      setProductLicenses(response.data);
    } catch (error) { console.error('Failed to fetch licenses:', error); }
  };

  const addLicenseField = () => setLicenses([...licenses, { content: '' }]);
  const removeLicenseField = (index) => setLicenses(licenses.filter((_, i) => i !== index));
  const updateLicenseField = (index, value) => {
    const newLicenses = [...licenses];
    newLicenses[index].content = value;
    setLicenses(newLicenses);
  };

  const handleAddLicenses = async () => {
    const validLicenses = licenses.filter(l => l.content.trim());
    if (validLicenses.length === 0) { toast.error('Add at least one license'); return; }
    try {
      for (const lic of validLicenses) {
        await api.post('/admin/licenses', { product_id: selectedProduct.id, content: lic.content.trim() });
      }
      toast.success(`Added ${validLicenses.length} license(s)`);
      setLicenses([{ content: '' }]);
      handleOpenLicenseDialog(selectedProduct);
      fetchData();
    } catch (error) { toast.error('Failed to add licenses'); }
  };

  const handleDeleteLicense = async (licenseId) => {
    try {
      await api.delete(`/admin/licenses/${licenseId}`);
      toast.success('License deleted');
      handleOpenLicenseDialog(selectedProduct);
      fetchData();
    } catch (error) { toast.error('Failed to delete license'); }
  };

  const handleDeleteSoldLicenses = async () => {
    if (!window.confirm('Delete ALL sold licenses for this product? This cannot be undone.')) return;
    setDeletingSold(true);
    try {
      await api.delete(`/admin/licenses/sold/${selectedProduct.id}`);
      toast.success('Sold licenses deleted');
      handleOpenLicenseDialog(selectedProduct);
      fetchData();
    } catch (error) { toast.error('Failed to delete sold licenses'); }
    finally { setDeletingSold(false); }
  };

  // Pagination logic
  const totalItems = products.length;
  const totalPages = viewAll ? 1 : Math.ceil(totalItems / PER_PAGE);
  const displayedProducts = viewAll 
    ? products 
    : products.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const soldLicensesCount = productLicenses.filter(l => l.is_sold).length;

  // Get full image URL
  const getImageUrl = (thumbnail) => {
    if (!thumbnail) return null;
    if (thumbnail.startsWith('http://') || thumbnail.startsWith('https://')) {
      return thumbnail;
    }
    return `${process.env.REACT_APP_BACKEND_URL}${thumbnail}`;
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-white font-['Outfit']">Products & Categories</h1>
        <div className="flex items-center gap-2">
          <Dialog open={showProductDialog} onOpenChange={(v) => { setShowProductDialog(v); if (!v) resetProductForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#FFD700] text-black font-bold hover:bg-[#E5C100]"><Plus className="w-4 h-4 mr-2" />Add Product</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-2 border-[#FFD700]/30 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-white font-bold text-xl">{selectedProduct ? 'Edit Product' : 'Add Product'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmitProduct} className="space-y-4">
                <div><Label className="text-[#FFD700] font-bold uppercase text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="bg-[#121212] border-2 border-white/20 text-white font-semibold focus:border-[#FFD700]" required /></div>
                <div><Label className="text-[#FFD700] font-bold uppercase text-xs">Subtitle</Label><Input value={form.subtitle} onChange={(e) => setForm({...form, subtitle: e.target.value})} className="bg-[#121212] border-2 border-white/20 text-white focus:border-[#FFD700]" /></div>
                <div><Label className="text-[#FFD700] font-bold uppercase text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-[#121212] border-2 border-white/20 text-white focus:border-[#FFD700]" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-[#FFD700] font-bold uppercase text-xs">Price (₦)</Label><Input type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} className="bg-[#121212] border-2 border-white/20 text-white focus:border-[#FFD700]" required /></div>
                  <div><Label className="text-[#FFD700] font-bold uppercase text-xs">Discount %</Label><Input type="number" value={form.discount} onChange={(e) => setForm({...form, discount: e.target.value})} className="bg-[#121212] border-2 border-white/20 text-white focus:border-[#FFD700]" /></div>
                </div>
                <div>
                  <Label className="text-[#FFD700] font-bold uppercase text-xs">Category</Label>
                  <Select value={form.category_id} onValueChange={(v) => setForm({...form, category_id: v})}>
                    <SelectTrigger className="bg-[#121212] border-2 border-white/20 text-white"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {categories.map((cat) => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Product Image - URL or Upload */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[#FFD700] font-bold uppercase text-xs">Product Image</Label>
                    <div className="flex gap-1">
                      <Button type="button" variant={imageInputType === 'url' ? 'default' : 'outline'} size="sm" onClick={() => setImageInputType('url')} className={imageInputType === 'url' ? 'bg-[#FFD700] text-black' : 'border-white/20 text-white'}>
                        <Link className="w-3 h-3 mr-1" />URL
                      </Button>
                      <Button type="button" variant={imageInputType === 'upload' ? 'default' : 'outline'} size="sm" onClick={() => setImageInputType('upload')} className={imageInputType === 'upload' ? 'bg-[#FFD700] text-black' : 'border-white/20 text-white'}>
                        <Upload className="w-3 h-3 mr-1" />Upload
                      </Button>
                    </div>
                  </div>
                  
                  {imageInputType === 'url' ? (
                    <div className="space-y-2">
                      <Input
                        value={form.thumbnail}
                        onChange={(e) => setForm({...form, thumbnail: e.target.value})}
                        placeholder="https://example.com/image.jpg"
                        className="bg-[#121212] border-2 border-white/20 text-white focus:border-[#FFD700]"
                      />
                      <p className="text-xs text-zinc-500">Paste an image URL from the web</p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4">
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id="product-image-upload" />
                      <label htmlFor="product-image-upload">
                        <Button type="button" variant="outline" className="border-2 border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10 font-bold" disabled={uploadingImage} asChild>
                          <span>{uploadingImage ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Uploading...</> : <><Upload className="w-4 h-4 mr-2" />Choose File</>}</span>
                        </Button>
                      </label>
                    </div>
                  )}
                  
                  {/* Image Preview */}
                  {form.thumbnail && (
                    <div className="mt-3 relative">
                      <img 
                        src={getImageUrl(form.thumbnail)} 
                        alt="Preview" 
                        className="w-full h-32 object-cover rounded-lg border-2 border-[#FFD700]/30"
                        onError={(e) => { e.target.src = ''; e.target.style.display = 'none'; }}
                      />
                      <Button type="button" variant="destructive" size="sm" className="absolute top-2 right-2" onClick={() => setForm({...form, thumbnail: ''})}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
                
                <Button type="submit" className="w-full bg-[#FFD700] text-black font-bold hover:bg-[#E5C100]">{selectedProduct ? 'Update' : 'Create'} Product</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={showCategoryDialog} onOpenChange={(v) => { setShowCategoryDialog(v); if (!v) { setCategoryForm({ name: '', description: '' }); setSelectedCategory(null); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-2 border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10 font-bold"><FolderOpen className="w-4 h-4 mr-2" />Add Category</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-2 border-[#FFD700]/30">
              <DialogHeader><DialogTitle className="text-white font-bold text-xl">{selectedCategory ? 'Edit Category' : 'Add Category'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmitCategory} className="space-y-4">
                <div><Label className="text-[#FFD700] font-bold uppercase text-xs">Name</Label><Input value={categoryForm.name} onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})} className="bg-[#121212] border-2 border-white/20 text-white focus:border-[#FFD700]" required /></div>
                <div><Label className="text-[#FFD700] font-bold uppercase text-xs">Description</Label><Textarea value={categoryForm.description} onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})} className="bg-[#121212] border-2 border-white/20 text-white focus:border-[#FFD700]" /></div>
                <Button type="submit" className="w-full bg-[#FFD700] text-black font-bold hover:bg-[#E5C100]">{selectedCategory ? 'Update' : 'Create'} Category</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#0A0A0A] border-2 border-[#FFD700]/30">
          <TabsTrigger value="products">Products ({totalItems})</TabsTrigger>
          <TabsTrigger value="categories">Categories ({categories.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-6 space-y-4">
          {/* Search Bar */}
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input placeholder="Search products..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-[#121212] border-white/10 text-white placeholder:text-zinc-500" />
          </div>

          {viewAll && totalItems > PER_PAGE && (
            <div className="flex items-center justify-between bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-lg p-3">
              <span className="text-sm text-[#FFD700]">Viewing all {totalItems} products</span>
              <Button variant="outline" size="sm" onClick={() => { setViewAll(false); setCurrentPage(1); }} className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10">Show Paginated</Button>
            </div>
          )}

          {products.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-8 text-center">
                <Package className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                <p className="text-zinc-500">{searchQuery ? `No products found matching "${searchQuery}"` : 'No products yet'}</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Products Grid - Boxes Layout */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {displayedProducts.map((product) => (
                  <Card key={product.id} className="bg-[#0A0A0A] border-2 border-[#FFD700]/20 hover:border-[#FFD700] transition-all hover:shadow-[0_0_20px_rgba(255,215,0,0.2)] group overflow-hidden">
                    <div className="relative h-28 bg-gradient-to-br from-[#121212] to-black">
                      {product.thumbnail ? (
                        <img 
                          src={getImageUrl(product.thumbnail)} 
                          alt={product.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-8 h-8 text-[#FFD700]/30" />
                        </div>
                      )}
                      {product.stock === 0 && (
                        <Badge className="absolute top-1 left-1 bg-red-500 text-white text-xs">Out of Stock</Badge>
                      )}
                      {product.has_discount && (
                        <Badge className="absolute top-1 right-1 bg-green-500 text-white text-xs">-{product.discount}%</Badge>
                      )}
                    </div>
                    <CardContent className="p-3">
                      <h3 className="font-bold text-white text-sm truncate" title={product.title}>{product.title}</h3>
                      <Badge className="bg-[#FFD700]/10 text-[#FFD700] text-xs mt-1">{product.category_name || 'Uncategorized'}</Badge>
                      <div className="flex items-center justify-between mt-2">
                        <span className="font-black text-[#FFD700]">{formatMoney(product.current_price)}</span>
                        <span className={`text-xs ${product.stock > 0 ? 'text-green-400' : 'text-red-400'}`}>{product.stock} keys</span>
                      </div>
                      <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="outline" size="sm" className="flex-1 h-7 text-xs border-[#FFD700]/50 text-[#FFD700]" onClick={() => handleOpenLicenseDialog(product)}>
                          <Key className="w-3 h-3 mr-1" />{product.stock}
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 w-7 p-0 border-white/20" onClick={() => editProduct(product)}><Edit className="w-3 h-3" /></Button>
                        <Button variant="outline" size="sm" className="h-7 w-7 p-0 border-red-500/50 text-red-500" onClick={() => handleDeleteProduct(product.id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {!viewAll && totalPages > 1 && (
                <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} perPage={PER_PAGE} onPageChange={setCurrentPage} onViewAll={() => setViewAll(true)} />
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="categories" className="mt-6 space-y-4">
          {categories.length === 0 ? <p className="text-zinc-500 text-center py-8">No categories yet</p> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.map((category) => (
                <Card key={category.id} className="bg-[#0A0A0A] border-2 border-[#FFD700]/20 hover:border-[#FFD700]/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#FFD700]/20 to-transparent rounded-lg flex items-center justify-center border border-[#FFD700]/30">
                        <FolderOpen className="w-5 h-5 text-[#FFD700]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-white truncate">{category.name}</p>
                        <p className="text-sm text-zinc-500">{category.product_count || 0} products</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button variant="outline" size="sm" className="flex-1 border-white/20 hover:border-white/50" onClick={() => editCategory(category)}><Edit className="w-3 h-3 mr-1" />Edit</Button>
                      <Button variant="outline" size="sm" className="border-red-500/50 text-red-500 hover:bg-red-500/10" onClick={() => handleDeleteCategory(category.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* License Management Dialog */}
      <Dialog open={showLicenseDialog} onOpenChange={(v) => { setShowLicenseDialog(v); if (!v) { setSelectedProduct(null); setLicenses([{ content: '' }]); setProductLicenses([]); } }}>
        <DialogContent className="bg-[#0A0A0A] border-2 border-[#FFD700]/30 max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-white font-bold text-xl">Manage Licenses - {selectedProduct?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 overflow-y-auto max-h-[60vh] pr-2">
            {/* Add New Licenses */}
            <div className="space-y-3">
              <Label className="text-[#FFD700] font-bold uppercase text-xs">Add New Licenses (One per field)</Label>
              {licenses.map((license, index) => (
                <div key={index} className="flex gap-2">
                  <Input value={license.content} onChange={(e) => updateLicenseField(index, e.target.value)} placeholder={`License key ${index + 1}`} className="bg-[#121212] border-2 border-white/20 text-white font-mono focus:border-[#FFD700]" />
                  {licenses.length > 1 && (
                    <Button type="button" variant="outline" size="icon" onClick={() => removeLicenseField(index)} className="border-red-500/50 text-red-500 hover:bg-red-500/10 flex-shrink-0"><X className="w-4 h-4" /></Button>
                  )}
                </div>
              ))}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={addLicenseField} className="border-2 border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10 font-bold"><Plus className="w-4 h-4 mr-2" />Add Another</Button>
                <Button type="button" onClick={handleAddLicenses} className="bg-[#FFD700] text-black font-bold hover:bg-[#E5C100]">Save Licenses</Button>
              </div>
            </div>

            {/* Existing Licenses */}
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-[#FFD700] font-bold uppercase text-xs">Existing Licenses ({productLicenses.length})</Label>
                {soldLicensesCount > 0 && (
                  <Button type="button" variant="outline" size="sm" onClick={handleDeleteSoldLicenses} disabled={deletingSold} className="border-red-500/50 text-red-500 hover:bg-red-500/10 text-xs">
                    {deletingSold ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}Delete {soldLicensesCount} Sold
                  </Button>
                )}
              </div>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {productLicenses.length === 0 ? (
                  <p className="text-zinc-500 text-center py-4">No licenses added yet</p>
                ) : productLicenses.map((lic) => (
                  <div key={lic.id} className={`flex items-center justify-between p-2 rounded-lg ${lic.is_sold ? 'bg-red-500/10 border border-red-500/30' : 'bg-[#121212] border border-white/10'}`}>
                    <div className="flex-1 min-w-0"><code className="text-sm text-white font-mono truncate block">{lic.content}</code></div>
                    <div className="flex items-center gap-2 ml-2">
                      {lic.is_sold && <Badge className="bg-red-500 text-white">Sold</Badge>}
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteLicense(lic.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
